import { isInactiveCompanyAdmin } from './_helpers';

const matchesAllowedPath = (pathname, allowedPath) =>
  pathname === allowedPath || pathname.startsWith(`${allowedPath}/`);

const hasRole = (user, role) => user?.roles?.includes(role);
const hasPermission = (user, permission) => user?.permissions?.includes(permission);

const isSuperAdmin = (user) => hasRole(user, 'super-admin');
const isCompanyAdmin = (user) => hasRole(user, 'company-admin');
const isReferralAttorney = (user) => user?.referral_attorney === 1;
const hasAdminAccess = (user) => hasPermission(user, 'is_admin_access');
const hasGlobalAdminAccess = (user) => hasPermission(user, 'is_global_admin');

const canAccessBillingSummary = (user) =>
  isCompanyAdmin(user) || isSuperAdmin(user);

const canAccessCompanySettings = (user) =>
  isCompanyAdmin(user) || isSuperAdmin(user);

const canAccessCompanyDirectory = (user) =>
  isSuperAdmin(user) || hasGlobalAdminAccess(user) || hasAdminAccess(user);

const canAccessCompanyDetail = (user) => isSuperAdmin(user);
const canAccessCompanyInvoicePreview = (user) => isSuperAdmin(user);
const canAccessCompanyPackages = (user) => isSuperAdmin(user);

const SUPER_ADMIN_ALLOWED_PATHS = [
  '/company/list',
  '/company/detail',
  '/company/detail/invoice-preview',
  '/account/home/user-profile',
  '/account/security/overview',
  '/company-billing/license',
  '/licenses',
  '/account/home/settings-sidebar',
  '/account/global/settings',
  '/discount-code',
  '/discount-code-usage'
];

/**
 * The firm requires 2FA and this user has not set it up (OL-157). The server
 * refuses everything else until they do; the SPA keeps them on the page where
 * 2FA is set up.
 */
const TWO_FACTOR_SETUP_PATH = '/account/security/overview';

const needsTwoFactorSetup = (user) =>
  !!user && !isSuperAdmin(user) && user?.security?.require_two_factor === true && Number(user?.is_2fa_enabled) !== 1;

const TWO_FACTOR_SETUP_ALLOWED_PATHS = [
  TWO_FACTOR_SETUP_PATH,
  '/account/home/user-profile',
];

const INACTIVE_COMPANY_ADMIN_ALLOWED_PATHS = [
  '/company-billing/license',
  '/account/home/user-profile',
  '/account/security/overview',
];

const SUPER_ADMIN_MENU_ITEMS = [
  {
    title: 'Companies',
    path: '/company/list',
  },
  {
    title: 'Licenses',
    path: '/licenses',
  },
  {
    title: 'Discount Code',
    path: '/discount-code',
  },
  {
    title: 'Discount Code Usage',
    path: '/discount-code-usage',
  }
];

const getAllowedPathsForUser = (user) => {
  if (isSuperAdmin(user)) {
    return SUPER_ADMIN_ALLOWED_PATHS;
  }

  if (needsTwoFactorSetup(user)) {
    return TWO_FACTOR_SETUP_ALLOWED_PATHS;
  }

  if (isInactiveCompanyAdmin(user)) {
    return INACTIVE_COMPANY_ADMIN_ALLOWED_PATHS;
  }

  return null;
};

const getRestrictedRedirectPath = (user) => {
  if (isSuperAdmin(user)) {
    return '/company/list';
  }

  if (needsTwoFactorSetup(user)) {
    return TWO_FACTOR_SETUP_PATH;
  }

  if (isInactiveCompanyAdmin(user)) {
    return '/company-billing/license';
  }

  return null;
};

const canAccessRestrictedPath = (user, pathname) => {
  const allowedPaths = getAllowedPathsForUser(user);

  if (!allowedPaths) {
    return true;
  }

  return allowedPaths.some((allowedPath) => matchesAllowedPath(pathname, allowedPath));
};

const shouldShowCompanySettingsEntry = (user) =>
  !isInactiveCompanyAdmin(user) && canAccessCompanySettings(user);

const shouldShowRecycleBinEntry = (user) =>
  !isSuperAdmin(user) && !isInactiveCompanyAdmin(user);

const shouldHideTopbarActions = (user) =>
  isReferralAttorney(user) || isSuperAdmin(user) || isInactiveCompanyAdmin(user);

export {
  INACTIVE_COMPANY_ADMIN_ALLOWED_PATHS,
  SUPER_ADMIN_ALLOWED_PATHS,
  SUPER_ADMIN_MENU_ITEMS,
  canAccessBillingSummary,
  canAccessCompanyDirectory,
  canAccessCompanyDetail,
  canAccessCompanyInvoicePreview,
  canAccessCompanyPackages,
  canAccessCompanySettings,
  canAccessRestrictedPath,
  getAllowedPathsForUser,
  getRestrictedRedirectPath,
  hasAdminAccess,
  hasGlobalAdminAccess,
  hasPermission,
  hasRole,
  isCompanyAdmin,
  isReferralAttorney,
  isSuperAdmin,
  matchesAllowedPath,
  needsTwoFactorSetup,
  TWO_FACTOR_SETUP_PATH,
  shouldHideTopbarActions,
  shouldShowCompanySettingsEntry,
  shouldShowRecycleBinEntry,
};
